<template>
  <div class="product-slug">
    <div class="container">
      <div class="product-slug__breadcrumbs">
        <Breadcrumbs
          :breadcrumbs="breadcrumbs"
        />
      </div>

      <template v-if="!isLoadProduct">
        <div class="product-slug__content">
          <div class="--left">
            <div class="--sticky">
              <SliderImages
                :product="product"
                :parentsCollections="parentsCollections"
                @openGallery="openGalleryModal"
              />
            </div>
          </div>
          <div class="--right">
            <Head :product="product" />
            <div class="--space" />

            <template v-if="isVisibleAdminInformation">
              <AdminInformation
                :product="product"
              />
              <div class="--space" />
            </template>

            <OptionsAction
              v-if="product"
              :product="product"
            />
            <div class="--divider" />
            <template v-if="isShowOtherProductsSection">
              <OtherProductsSection
                :products="otherProductsCollection"
                :loading="isLoadOtherProductsCollection"
                :goal="'product_slider_other_products'"
              />
              <div class="--divider" />
            </template>
            <Characteristics :product="product" />
            <template v-if="isProductInCollection">
              <div
                id="collections"
                class="--divider"
              />
              <ProductInCollection
                :product="product"
                @change="changeActiveSlugCollection"
              />
            </template>
            <div class="--divider" />
            <DeliveryTerm :product="product" />
            <div class="--divider" />
            <template
              v-for="(characteristic) in characteristicSection"
              :key="`characteristicSection-${characteristic?.slug}`"
            >
              <CharacteristicSection
                :characteristic="characteristic"
              />
              <div class="--divider" />
            </template>
            <template v-if="isAssemblyInstructions">
              <AssemblyInstructions
                :documents="documents"
              />
              <div class="--divider" />
            </template>
            <AdditionalServices
              :delivery-tab-info="deliveryTabInfo"
            />
            <div class="--divider" />
            <Reviews
              v-if="product"
              :product="product"
              model="Product"
              page="Product"
            />
            <div class="--divider" />
            <Questions
              :product="product"
              model="Product"
              :page="'Product'"
            />
            <div class="--divider" />
            <Consultations
              :product-name="product?.name"
            />
          </div>
        </div>


        <div ref="refElementForVisibleOtherProducts"/>
        <template v-if="isVisibleOtherProducts">
          <div class="product-slug__products">
            <template v-if="(state.productsRelated || []).length > 0">
              <SliderRelatedProducts
                :products="state.productsRelated"
                type="product"
                title="Возможно, вас заинтересует"
                :goal="String('product_slider_related')"
              />
            </template>
            <template v-if="(state.productsSame || []).length > 0">
              <SliderRelatedProducts
                :products="state.productsSame"
                type="product"
                title="Похожие товары"
                :goal="String('product_slider_same')"
              />
            </template>
            <template v-if="(recentlyViewedProducts.length > 0)">
              <SliderRelatedProducts
                :products="recentlyViewedProducts"
                type="products"
                title="Вы ранее просматривали товары"
                linkUrl="/recently"
                linkTitle="Посмотреть все просмотренные"
                :goal="String('product_slider_viewed_products')"
              />
            </template>
            <template v-if="(recentlyViewedCollections.length > 0)">
              <SliderRelatedProducts
                :products="recentlyViewedCollections"
                type="collections"
                title="Вы ранее просматривали коллекции"
                linkUrl="/recently"
                linkTitle="Посмотреть все просмотренные"
                :goal="String('product_slider_viewed_collections')"
              />
            </template>
          </div>
        </template>
        <WeRecommendViewingCategories
          :product="product"
        />
      </template>
      <template v-if="isLoadProduct">
        <div class="product-slug__content">
          <div class="--left">
            <div class="loading-skeleton loading-content__image" />
          </div>
          <div class="--right">
            <div class="loading-skeleton" />
            <div class="loading-skeleton loading-content__name" />
            <div class="loading-skeleton loading-content__description" />
          </div>
        </div>
      </template>
      <default-popup>
        <template
          v-if="product"
          #top
        >
          <div>
            <ProductCardForModal :product="product" />
          </div>
        </template>
      </default-popup>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, type ComputedRef, onMounted, ref } from 'vue';
import { getCharacteristicFromFull } from '~/utils/getCharacteristic';
import useProduct from '#sitis/composables/useSitisProduct';
import Breadcrumbs from '~/components/UI/Breadcrumbs.vue';
import Head from '~/components/products/Head.vue';
import OptionsAction from '~/components/products/OptionsAction.vue';
import Characteristics from '~/components/products/Characteristics.vue';
import ProductInCollection from '~/components/products/ProductInCollection.vue';
import AssemblyInstructions, { type IDocument } from '~/components/products/AssemblyInstructions.vue';
import DeliveryTerm from '~/components/collection/DeliveryTerm.vue';
import CharacteristicSection from '~/components/collection/CharacteristicSection.vue';
import AdditionalServices from '~/components/collection/AdditionalServices.vue';
import Consultations from '~/components/collection/Consultations.vue';
import Reviews from '~/components/collection/Reviews.vue';
import DefaultPopup from '~/components/common/DefaultPopup.vue';
import ProductCardForModal from '~/components/products/ProductCardForModal.vue';
import {useNuxtApp, useServerSeoMeta} from '#imports';
import useEcommerce from '~/composables/useEcommerceYandex';
import useSitisUserManager from '~/composables/useSitisUserManager';
import { type IProduct, Product, type ProductCharacteristics, type ProductCharacteristic } from '#sitis/internal/controllers/products/models/Product';
import { useIntersectionObserver } from '@vueuse/core';
import Questions from "~/components/collection/Questions.vue";
import WeRecommendViewingCategories from "~/components/products/WeRecommendViewingCategories.vue";

const SliderImages = defineAsyncComponent(() => import("~/components/products/SliderImages.vue"));
const TemplateListRecently = defineAsyncComponent(() => import("~/components/common/TemplateListRecently.vue"));

const $router = useRouter();
const route = useRoute();
const { setSEOTitles } = useSitisCMSPage();
const AdminInformation = defineAsyncComponent(() => import('~/components/products/AdminInformation.vue'));
const OtherProductsSection = defineAsyncComponent(() => import('~/components/products/OtherProductsSection.vue'));
const SliderRelatedProducts = defineAsyncComponent(() => import('~/components/products/SliderRelatedProducts.vue'));
const { addEcommerce } = useEcommerce();
// settings
const {
  state,

  getProduct,
  getProductsSame,
  getProductsRelated,
  getProductsRecently
} = useProduct({
  extensions: ['images', 'promotions']
});

const {
  $api,
  $viewport
} = useNuxtApp();

// computed
const documents: ComputedRef<IDocument[]> = computed(() => (product.value?.files || []).map((file: any) => ({
  url: file.url,
  name: file.name,
  caption: file.caption
})));

const breadcrumbs = computed(() => [
  { title: 'Главная', path: '/' },
  { title: 'Каталог', path: '/catalog' },
  { title: product.value?.name, path: product.value?.url }
]);
const isProductInCollection = computed(() => (product.value?.productSetParents || []).length > 0);
const characteristicSection = computed(() => {
  let _characteristics: any = [];
  (product?.value?.characteristics || []).map((_item: any) => {
    _characteristics.push(...(_item?.characteristics || []))
  });
  _characteristics = _characteristics.filter((t: any) => t.viewTemplate === 'for_custom_sections');
  _characteristics = _characteristics.filter((t: any) => !['in_stock_product_status'].includes(t.slug))
  return _characteristics
});
const isAssemblyInstructions = computed(() => Boolean((product.value?.files || []).length > 0));
const productImage = useImage();

const {
  data: product,
  pending: isLoadProduct,
  status: statusLoadProduct,
} = await useLazyAsyncData(
  'getProductSlug',
  () => getProduct([
    'bonus_value_total_exact',
    'files', 'options', 'categories', 'short_characteristics',
    'productSetParents', 'productSetParents', 'additionalCategories',
  ]),
  { watch: [() => route.params.slug] }
);
const {
  userManagerInfo
} = useSitisUserManager();

const checkProductFound = () => {
  if (isLoadProduct.value) {
    return;
  }
  if (String(product.value?.isProductSet) === 'true' && !process.client) {
    return navigateTo(`/collection/${product?.value?.slug}`, {
      redirectCode: 301
    })
  }
  if (product.value?.id) {
    return;
  }
  throw showError({
    statusCode: 404,
    message: 'Товар<br/>не найден',
    statusMessage: 'Товар<br/>не найден'
  });
};

watch(isLoadProduct, () => {
  checkProductFound();
  initSeoMeta();
  getOtherProductsCollection();
  getRecentlyViewed();
});
onMounted(async () => {
  await getParentCollections();
  await getProductsSame(['short_characteristics', 'promotions', 'promotions.icon']);
  await getProductsRelated(['short_characteristics', 'promotions', 'promotions.icon']);
  await getProductsRecently(['short_characteristics', 'promotions', 'promotions.icon']);
  await getRecentlyViewed();
  addEcommerce('detail', product.value);
});

checkProductFound();

function initSeoMeta () {
  const title = product.value?.seo?.title || product.value?.name || '';
  const description = product.value?.seo?.description || '';

  setSEOTitles({
    seoTitle: title,
    seoDescription: description
  });

  useServerSeoMeta({
    ogImage: () => productImage(String(product.value?.imageId))
  });
}

initSeoMeta();
const { openGallery } = useGallery();

function openGalleryModal (activeIndex: number, imagesIndexWithDots: number[], parentCollections: IProduct[]) {
  openGallery({
    imagesIds: product.value?.imageIds.concat(parentsCollections.value.map((item: IProduct) => item.imageId)),
    videos: product.value?.videos,
    indexActiveSlide: activeIndex,
    itemsIndexWithDots: imagesIndexWithDots,
    products: parentCollections,
    product: product.value
  });
}

function availabilityDate () {
  const todayDate = new Date();
  const newDay = todayDate.setDate(todayDate.getDate() + 1);
  return new Date(newDay).toISOString();
}

const deliveryTabInfo: ComputedRef<string> = computed(() => {
  return getCharacteristicFromFull((product.value?.characteristics || []), 'delivery_tab_info')?.values?.[0]?.value || ''
});

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.value?.name || 'name',
  description: product.value?.description || '',
  image: productImage(String(product.value?.imageId)),
  offers: {
    '@type': 'Offer',
    price: product.value?.price,
    priceCurrency: 'RUB',
    sku: product.value?.sku || '',
    availability: 'https://schema.org/PreOrder',
    priceValidUntil: availabilityDate()
  },
  brand: {
    '@type': 'Brand',
    name: product.value?.brand?.name || ''
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    bestRating: 5,
    worstRating: 4,
    ratingValue: 4.89,
    ratingCount: 843
  }
}));


const isVisibleAdminInformation: ComputedRef<boolean> = computed(() => {
  return Boolean(userManagerInfo.value?.id)
});

const isShowOtherProductsSection: ComputedRef<boolean> = computed(() => {
  if (Boolean((product?.value?.productSetParents || []).length <= 0)) {
    return false
  }
  if (!isLoadOtherProductsCollection.value && otherProductsCollection.value.length <= 0) {
    return false
  }
  return true
});
const otherProductsCollection: Ref<any[]> = ref([]);
const selectedCollectionSlug: Ref<string> = ref('');
const isLoadOtherProductsCollection: Ref<boolean> = ref(true);
const getOtherProductsCollection = async () => {
  if (isLoadProduct.value) {
    return
  }

  const collections: any = product?.value?.productSetParents || [];
  const collectionSlug: string = selectedCollectionSlug.value || collections?.[0]?.slug || '';
  if (!collectionSlug) {
    return
  }

  isLoadOtherProductsCollection.value = true;
  const res = await $api.agent.get(`/other-product-of-collection?product_slug=${ product.value?.slug }&collection_slug=${ collectionSlug }`).then((res: any) => {
    return (res?._data || []).map((t: any) => {
      t.price = t.price_new;
      return new Product(t)
    })
  }).catch(() => []);
  otherProductsCollection.value = res;
  isLoadOtherProductsCollection.value = false;
};
const changeActiveSlugCollection = async (slug: string) => {
  selectedCollectionSlug.value = slug;
  await getOtherProductsCollection();
};
onMounted(async () => {
  await getOtherProductsCollection();
});

const recentlyViewedProducts: Ref<IProduct[]> = ref([]);
const recentlyViewedCollections: Ref<IProduct[]> = ref([]);
const getRecentlyViewed = async () => {
  let _products = await $api.agent.get(`/recently-viewed?is_product_set=0&expand=short_characteristics,promotions,promotions.icon`).then((res: any) => {
    return (res?._data || []).map((t: any) => new Product(t));
  }).catch(() => []);
  _products = _products.filter((t: IProduct) => Boolean(String(t.id) !== String(product.value?.id)));
  recentlyViewedProducts.value = _products;

  let _collections = await $api.agent.get(`/recently-viewed?is_product_set=1&expand=short_characteristics,promotions,promotions.icon`).then((res: any) => {
    return (res?._data || []).map((t: any) => new Product(t));
  }).catch(() => []);
  _collections = _collections.filter((t: IProduct) => Boolean(String(t.id) !== String(product.value?.id)));
  recentlyViewedCollections.value = _collections;
};

// получаем родительские коллекции
const productParents: ComputedRef<any> = computed(() => {
  if (product.value && product.value.productSetParents) {
    return product.value.productSetParents.slice (0, 3) // as IProduct
  }
  return []
})

const dontAddPhotosCollections = computed(() => {
  return (product.value?.characteristics?.find((t: ProductCharacteristics) => t.name === 'Внутреннее, контент заполняет'))?.characteristics?.find((char: ProductCharacteristic) => char.slug === 'ne-dobavlyat-foto-kollekcii-k-tovaru')?.values?.[0]?.value === 'да'
})

const parentsCollections: any = ref([]);

// делаем запрос родительских коллекиций и складываем в массив
const getParentCollections = async function () {
  const expand = ['images', 'external_id'].join(',')
  if (productParents.value && productParents.value.length > 0 && !dontAddPhotosCollections.value) {
    let _parentsCollections: any = [];
    for (const collection of (productParents.value || [])) {
      const _res = await $api.agent.get(`/products/${ collection.slug }?expand=${ expand }`).then((res: any) => {
        return res._data || []
      }).catch(() => []);
      _parentsCollections.push({ ...new Product(_res), futureImageIndex: product?.value?.imageIds?.length + _parentsCollections.length + 1})
    }
    parentsCollections.value = _parentsCollections;
  }
}

watch(() => product.value?.id, async (id) => {
  if (!id) return;
  parentsCollections.value = [];
  await getParentCollections();
}, { immediate: true });

// const checkIsProductSet = async function() {
//   if (product.value?.isProductSet) {
//     // $router.replace(`/collection/${product?.value?.slug}`)
//     return navigateTo(`/collection/${product?.value?.slug}`)
//   }
// }



const refElementForVisibleOtherProducts: Ref<HTMLDivElement | null> = ref(null);
const isVisibleOtherProducts: Ref<boolean> = ref(false);
const { stop: stopVisibleOtherProducts } = useIntersectionObserver(
  refElementForVisibleOtherProducts,
  async ([{ isIntersecting }]) => {
    if (isIntersecting && !isVisibleOtherProducts.value) {
      isVisibleOtherProducts.value = true;
    }
  },
  { rootMargin: "60%" }
);
onBeforeUnmount(() => {
  stopVisibleOtherProducts();
});
</script>

<style lang="scss">
.product-slug {
	margin-bottom: 70px;
}

.product-slug__breadcrumbs {
	margin-top: -5px;
	margin-bottom: 11px;
}

.product-slug__content {
	display: flex;

	.--left {
		flex: 1;
		position: relative;
    z-index: 1;
	}

	.--right {
		width: 100%;
		max-width: 557px;
		margin-left: 40px;
    z-index: 1;

		& > .--space {
			margin-top: 16px;
		}

		& > .--divider {
      position: relative;
			margin-top: 16px;
			margin-bottom: 16px;
			width: 100%;
			height: 1px;
			background-color: #F4F3F1;
		}
	}

	.--sticky {
		position: sticky;
		top: 139px;
	}
}

.product-slug__products {
	display: flex;
	flex-direction: column;
	margin-top: 180px;

	& > * {
		margin-top: 120px;

		&:first-child {
			margin-top: 0;
		}
	}
}

.product-slug__other-products {
	.--title {
		margin-bottom: 32px;
		color: black;
	}

	.--items {
		display: flex;
		flex-wrap: wrap;
		margin-top: -64px;
		margin-left: -20px;
		width: 100%;

		& > * {
			width: calc(100% / 4 - 20px);
			margin-top: 64px;
			margin-left: 20px;
		}
	}
}

.collection-info-section {
	&.--open {
		.collection-info-section__head img {
			transform: rotate(180deg);
		}

		.collection-info-section__body {
			display: block;
		}
	}
}

.collection-info-section__head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	cursor: pointer;
	user-select: none;

	span {
		color: black;
	}
}

.collection-info-section__body {
	display: none;
	margin-top: 16px;
	color: #989898;
}

.loading-content__image {
	width: 100%;
	border-radius: 20px;
	overflow: hidden;

	&:before {
		content: "";
		float: left;
		padding-top: 55.547%;
	}
}

.loading-content__name {
	height: 29px;
	border-radius: 10px;
	overflow: hidden;

	&:before {
		content: "";
		float: left;
		padding-top: 55.547%;
	}
}

.loading-content__description {
	height: 220px;
	margin-top: 24px;
	border-radius: 10px;
	overflow: hidden;

	&:before {
		content: "";
		float: left;
		padding-top: 55.547%;
	}
}

@media (max-width: 1699px) {
	.product-slug__content {
		.--right {
			max-width: 501px;
		}
	}
}

@media (max-width: 1599px) {
  .product-slug__other-products .--items > * {
    width: calc(100% / 3 - 20px);
  }
}

@media (max-width: 1359px) {
	.product-slug__content {
		.--right {
			max-width: 460px;
			margin-left: 32px;
		}
	}
	.product-slug__other-products {
		.--items {
			& > * {
				width: calc(100% / 2 - 20px);
			}
		}
	}
}

@media (max-width: 1199px) {
  .product-slug__content .--right {
    max-width: 380px;
  }
}

@media (max-width: 1023px) {
	.product-slug__breadcrumbs {
		display: none;
	}
	.product-slug__content {
		flex-direction: column;

		.--right {
			margin-left: 0;
			max-width: initial;
		}

		.--right .--divider {
			margin: 0;
		}
	}
	.product-options-action {
		margin-bottom: 16px;
	}

	.collection-info-section {
		&.--open {
			padding-bottom: 22px;
		}
	}
	.collection-info-section__head {
		padding: 16px 0;
	}

	.product-slug__products {
		margin-top: 80px;

		& > * {
			margin-top: 80px;
		}
	}
	.product-slug__other-products {
		.--items {
			margin-top: -16px;
			margin-left: -16px;

			& > * {
				width: calc(100% / 2 - 16px);
				margin-top: 16px;
				margin-left: 16px;
			}
		}
	}
}

@media (max-width: 639px) {
	.product-slug__other-products {
		.--items {
			& > * {
				width: calc(100% - 16px);
			}
		}
	}
}
</style>
